import { useEffect, useRef, useState } from "react";

/*
 * ShardImage · canvas-based "mirror shard" reveal.
 *
 * The image is chopped into N diagonal strips. Each strip starts offset
 * perpendicular to the cut direction, then eases back to alignment over
 * ~duration seconds. Once resolved the canvas renders the image pixel-
 * perfect. Matches the Function Health / "flute" hero effect.
 *
 * Uses <canvas> with ctx.rotate + ctx.clip so cuts are geometrically
 * precise regardless of container size or DPR.
 */
const ShardImage = ({
  src,
  alt = "",
  className = "",
  objectPosition = "center center", // "75% 50%" or "center center"
  strips = 14,
  angle = 62, // degrees measured from horizontal (0 = →, 90 = ↑)
  offset = 140, // px of perpendicular offset for the largest shard (screen px)
  duration = 3.0, // seconds
  delay = 0.15,
  "data-testid": testid,
}) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const imgElRef = useRef(null);

  // Load the source image once
  useEffect(() => {
    const img = new Image();
    // Same-origin asset · no CORS needed. Setting crossOrigin causes a
    // second cached copy on some browsers and can trigger a re-mount later.
    img.decoding = "async";
    img.onload = () => {
      imgElRef.current = img;
      setImgLoaded(true);
    };
    img.onerror = () => {
      imgElRef.current = null;
      setImgLoaded(false);
    };
    img.src = src;
  }, [src]);

  // Animation loop
  useEffect(() => {
    if (!imgLoaded) return;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const ctx = canvas.getContext("2d");
    const img = imgElRef.current;
    if (!ctx || !img) return;

    let rafId = 0;
    let startTs = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Prefer-reduced-motion → draw once, no shards
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const rad = (angle * Math.PI) / 180;

    // Parse objectPosition ("center center" or "75% 50%")
    const parsePos = (str) => {
      const parts = String(str).trim().split(/\s+/);
      const map = { left: "0%", center: "50%", right: "100%", top: "0%", bottom: "100%" };
      const px = parseFloat(map[parts[0]] ?? parts[0]);
      const py = parseFloat(map[parts[1]] ?? parts[1]);
      return {
        x: isFinite(px) ? px / 100 : 0.5,
        y: isFinite(py) ? py / 100 : 0.5,
      };
    };
    const objPos = parsePos(objectPosition);

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
    };

    // Compute object-cover draw rect for the source image
    const computeCover = () => {
      const cw = canvas.width;
      const ch = canvas.height;
      const ir = img.width / img.height;
      const cr = cw / ch;
      let dw, dh;
      if (ir > cr) {
        dh = ch;
        dw = dh * ir;
      } else {
        dw = cw;
        dh = dw / ir;
      }
      const maxDx = cw - dw; // negative or zero
      const maxDy = ch - dh; // negative or zero
      const dx = maxDx * objPos.x;
      const dy = maxDy * objPos.y;
      return { dx, dy, dw, dh };
    };

    const easeOutExpo = (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    // Hold shards visible for the first ~35% of the animation, then rapidly
    // resolve. Gives the effect enough time to register before it eases away.
    const shardEase = (t) => {
      if (t <= 0.35) return 0;
      const u = (t - 0.35) / 0.65;
      return easeOutCubic(u);
    };

    // Precompute per-strip parameters
    const stripParams = Array.from({ length: strips }).map((_, i) => {
      const t01 = (i + 0.5) / strips; // 0..1 center of the band
      // Alternating direction, magnitude weighted by distance from center
      const dir = i % 2 === 0 ? 1 : -1;
      const centerBias = 0.4 + Math.abs(t01 - 0.5) * 1.2;
      const magnitude = dir * centerBias * offset;
      // Stagger start
      const stagger = (i / strips) * 0.35; // seconds spread over 35% of duration
      return { t01, magnitude, stagger };
    });

    const draw = (elapsedMs) => {
      const cw = canvas.width;
      const ch = canvas.height;
      const cover = computeCover();
      const cx = cw / 2;
      const cy = ch / 2;

      ctx.clearRect(0, 0, cw, ch);

      // Extent for the strip clipping rects (in rotated space we need to
      // span the whole rotated canvas). Diagonal of canvas × 1.3 is safe.
      const diag = Math.sqrt(cw * cw + ch * ch);
      const stripPitch = diag / strips; // strip thickness perpendicular to cut

      const secs = elapsedMs / 1000;

      for (let i = 0; i < strips; i++) {
        const p = stripParams[i];
        // Local progress for this strip: 0..1
        let local = (secs - delay - p.stagger) / duration;
        if (local < 0) local = 0;
        if (local > 1) local = 1;
        const eased = reduce ? 1 : shardEase(local);
        const currentOffsetPx = p.magnitude * (1 - eased) * dpr; // px in canvas space

        ctx.save();
        // Rotate around center so cuts become horizontal
        ctx.translate(cx, cy);
        ctx.rotate(-rad);
        // Clip to horizontal strip in rotated space
        const yStart = -diag / 2 + i * stripPitch;
        ctx.beginPath();
        ctx.rect(-diag, yStart, diag * 2, stripPitch + 0.5); // +0.5 to avoid seams
        ctx.clip();
        // Apply perpendicular offset (in rotated space this is +y direction)
        ctx.translate(0, currentOffsetPx);
        // Rotate back and translate back to draw the image normally
        ctx.rotate(rad);
        ctx.translate(-cx, -cy);
        // Draw the image with object-cover framing
        ctx.drawImage(img, cover.dx, cover.dy, cover.dw, cover.dh);
        ctx.restore();
      }

      // Diagonal seam highlights (soft white lines along each cut · fade out)
      const seamAlpha = reduce ? 0 : Math.max(0, 1 - secs / (duration + 0.4));
      if (seamAlpha > 0) {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(-rad);
        ctx.strokeStyle = `rgba(255,255,255,${0.28 * seamAlpha})`;
        ctx.lineWidth = 1 * dpr;
        for (let i = 1; i < strips; i++) {
          const y = -diag / 2 + i * stripPitch;
          ctx.beginPath();
          ctx.moveTo(-diag, y);
          ctx.lineTo(diag, y);
          ctx.stroke();
        }
        ctx.restore();
      }

      // Frosted wash of finely-spaced diagonal lines · fades away
      const washAlpha = reduce ? 0 : Math.max(0, 1 - secs / (duration + 0.2));
      if (washAlpha > 0) {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(-rad);
        ctx.strokeStyle = `rgba(255,255,255,${0.06 * washAlpha})`;
        ctx.lineWidth = 1 * dpr;
        const spacing = 22 * dpr;
        for (let y = -diag; y < diag; y += spacing) {
          ctx.beginPath();
          ctx.moveTo(-diag, y);
          ctx.lineTo(diag, y);
          ctx.stroke();
        }
        ctx.restore();
      }
    };

    const tick = (ts) => {
      if (!startTs) startTs = ts;
      const elapsed = ts - startTs;
      draw(elapsed);
      const done = elapsed / 1000 > delay + duration + 0.6;
      if (!done) {
        rafId = requestAnimationFrame(tick);
      } else {
        // Draw the resolved frame one more time to be safe
        draw((delay + duration + 1) * 1000);
      }
    };

    const onResize = () => {
      resize();
      // Redraw the current frame instantly so image stays put on resize
      draw(startTs ? performance.now() - startTs : 0);
    };

    resize();
    // First paint before RAF kicks in
    draw(0);
    rafId = requestAnimationFrame(tick);
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
    };
    // Intentionally re-run when key parameters change or image loads
  }, [imgLoaded, angle, offset, duration, delay, strips, objectPosition]);

  return (
    <div
      ref={wrapRef}
      className={`relative overflow-hidden ${className}`}
      data-testid={testid}
      role="img"
      aria-label={alt}
    >
      {/* Fallback: while the image is loading show a still <img> so there
          is no flash of empty background. Once loaded, the canvas covers it. */}
      <img
        src={src}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition }}
        aria-hidden="true"
      />
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute inset-0 h-full w-full"
      />
    </div>
  );
};

export default ShardImage;
